import type {
  PlaygroundRun,
  PlaygroundSettings,
  PromptTestSuite
} from "./types"

const SUITE_KEY = "qpien.playground.suite.v1"
const RUNS_KEY = "qpien.playground.runs.v1"
const SETTINGS_KEY = "qpien.playground.settings.v1"

const MAX_STORED_RUNS = 15
const MAX_STORED_OUTPUT_CHARS = 6000

function key(prefix: string, promptName: string) {
  return `${prefix}:${promptName || "__unsaved__"}`
}

function read<T>(storageKey: string): T | null {
  try {
    const raw = localStorage.getItem(storageKey)
    return raw ? (JSON.parse(raw) as T) : null
  } catch {
    return null
  }
}

function write(storageKey: string, value: unknown): boolean {
  try {
    localStorage.setItem(storageKey, JSON.stringify(value))
    return true
  } catch {
    return false
  }
}

/** Every model runs with the same output budget unless it is overridden. */
export const DEFAULT_MAX_TOKENS = 20000

export const DEFAULT_SETTINGS: PlaygroundSettings = {
  query: "",
  variables: {},
  conditions: {},
  previousMessages: [],
  modelOverride: "",
  temperature: "",
  maxTokens: DEFAULT_MAX_TOKENS,
  timeoutMs: "",
  repeats: 1,
  concurrency: 3,
  judgeModel: ""
}

export function loadSettings(promptName: string): PlaygroundSettings {
  const stored = read<Partial<PlaygroundSettings>>(
    key(SETTINGS_KEY, promptName)
  )
  const settings = { ...DEFAULT_SETTINGS, ...(stored ?? {}) }
  return {
    ...settings,
    conditions: settings.conditions ?? {},
    // Older saved settings predate the shared 20k budget.
    maxTokens:
      settings.maxTokens === "" ? DEFAULT_MAX_TOKENS : settings.maxTokens
  }
}

export function saveSettings(promptName: string, settings: PlaygroundSettings) {
  write(key(SETTINGS_KEY, promptName), settings)
}

export function emptySuite(promptName: string): PromptTestSuite {
  return { version: 1, promptName, cases: [] }
}

export function loadSuite(promptName: string): PromptTestSuite {
  const stored = read<PromptTestSuite>(key(SUITE_KEY, promptName))
  if (!stored || !Array.isArray(stored.cases)) {
    return emptySuite(promptName)
  }
  return { ...stored, promptName, version: 1 }
}

export function saveSuite(promptName: string, suite: PromptTestSuite) {
  write(key(SUITE_KEY, promptName), suite)
}

export function loadRuns(promptName: string): PlaygroundRun[] {
  return read<PlaygroundRun[]>(key(RUNS_KEY, promptName)) ?? []
}

function truncateRun(run: PlaygroundRun): PlaygroundRun {
  return {
    ...run,
    snapshot: {
      ...run.snapshot,
      systemPrompt: run.snapshot.systemPrompt.slice(0, MAX_STORED_OUTPUT_CHARS)
    },
    results: run.results.map((result) => ({
      ...result,
      attempts: result.attempts.map((attempt) => ({
        ...attempt,
        output: attempt.output.slice(0, MAX_STORED_OUTPUT_CHARS),
        parsed: undefined
      }))
    }))
  }
}

/** Prepends the run and trims history until it fits in localStorage. */
export function saveRun(
  promptName: string,
  run: PlaygroundRun
): PlaygroundRun[] {
  const storageKey = key(RUNS_KEY, promptName)
  let history = [truncateRun(run), ...loadRuns(promptName)].slice(
    0,
    MAX_STORED_RUNS
  )

  while (history.length > 0 && !write(storageKey, history)) {
    history = history.slice(0, history.length - 1)
  }

  return history
}

export function deleteRun(promptName: string, runId: string): PlaygroundRun[] {
  const history = loadRuns(promptName).filter((run) => run.id !== runId)
  write(key(RUNS_KEY, promptName), history)
  return history
}

export function clearRuns(promptName: string): PlaygroundRun[] {
  write(key(RUNS_KEY, promptName), [])
  return []
}
